import { useCallback } from "react"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import { noop, get } from "lodash"
import { Clear, OpenInNew, Tonality } from "@mui/icons-material"
import { Box, IconButton } from "@mui/material"
import classNames from "classnames"
import Results from "../results"
import Tooltip from "components/material/tooltip"
import DropdownIcon from "components/dropdown-icon"
import { getCompositeSecurityTooltip, isCompositeBase as isCompositeBaseHelper } from "helpers/compositeSecurities"
import { getIsBenchmarkEnabled } from "selectors/features"
import "./style.scss"

export const SELECT_BENCHMARK_PLACEHOLDER = "Benchmark"

const PortfolioBenchmarkSelector = (props) => {
  const {
    baseDropdownVisible,
    alternateLayout,
    toggleActive,
    handleBaseDropdown,
    isCompositeBase,
    placeholderText,
    openCompositeIcon,
  } = props

  const tooltipTitle = baseDropdownVisible ? "" : SELECT_BENCHMARK_PLACEHOLDER

  return (
    <div className="base-input-wrapper">
      <div className={classNames("icon-wrapper", { "drop-down-visible": baseDropdownVisible })} onClick={!alternateLayout ? toggleActive : noop}>
        <Tonality fontSize="small" />
      </div>
      <Tooltip title={tooltipTitle}>
        <div className="input-wrapper">
          <input
            id="portfolio-benchmark-input"
            type="text"
            onClick={handleBaseDropdown}
            className={classNames("benchmark-selector base-input", { "composite-base": isCompositeBase, "drop-down-visible": baseDropdownVisible, "alternate-layout": alternateLayout })}
            placeholder={placeholderText}
            title="Search for Benchmark"
            value={placeholderText}
            readOnly
          />
          <DropdownIcon isOpen={baseDropdownVisible} />
        </div>
      </Tooltip>
      {isCompositeBase && openCompositeIcon}
    </div>
  )
}

const baseBenchmarkPropTypes = {
  alternateLayout: PropTypes.bool,
  handleBaseDropdown: PropTypes.func.isRequired,
  isCompositeBase: PropTypes.bool.isRequired,
  placeholderText: PropTypes.string.isRequired,
  toggleActive: PropTypes.func.isRequired,
  baseDropdownVisible: PropTypes.bool.isRequired,
  openCompositeIcon: PropTypes.node,
}

PortfolioBenchmarkSelector.propTypes = {
  ...baseBenchmarkPropTypes,
}

const DefaultBenchmarkSelector = (props) => {
  const {
    placeholderText,
    handleBaseDropdown,
    toggleActive,
    isCompositeBase,
    openCompositeIcon,
    baseDropdownVisible,
    selectedBase,
  } = props

  const tooltipTitle = baseDropdownVisible ? "" : SELECT_BENCHMARK_PLACEHOLDER

  const benchmarkField = (
    <Tooltip title={tooltipTitle}>
      <div className="benchmark-selector clickable-selector" onClick={handleBaseDropdown}>
        <span>{placeholderText}</span>
        <DropdownIcon isOpen={baseDropdownVisible} />
      </div>
    </Tooltip>
  )

  return (
    <Box className="base-input-wrapper">
      <Tonality className="base-selector-icon" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {benchmarkField}
        {selectedBase && (
          <Tooltip title="Clear Benchmark">
            <IconButton onClick={toggleActive} size="small" className="benchmark-clear-button">
              <Clear fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {isCompositeBase && openCompositeIcon}
    </Box>
  )
}

DefaultBenchmarkSelector.propTypes = {
  ...baseBenchmarkPropTypes,
  selectedBase: PropTypes.object,
}

export const BenchmarkSelector = ({
  isPortfolio = false,
  ...props
}) => {
  return isPortfolio ? (
    <PortfolioBenchmarkSelector {...props} />
  ) : (
    <DefaultBenchmarkSelector {...props} />
  )
}

BenchmarkSelector.propTypes = {
  ...baseBenchmarkPropTypes,
  isPortfolio: PropTypes.bool,
  selectedBase: PropTypes.object,
}

function BaseSearch({
  searchValue,
  portfolioBenchmark,
  selectedBase,
  baseSearch,
  baseInputFocus,
  baseInputBlur,
  baseDropdownVisible,
  toggleActive,
  searchResults,
  setSelectedBase,
  baseHistory,
  alternateLayout,
  openCompositeSecurity,
  modelSupportsComposites,
  useDefaultActiveBenchmarkSelector = true,
  modelId,
}) {
  const isBenchmarkEnabled = useSelector(getIsBenchmarkEnabled)
  const selectedBaseName = get(selectedBase, "name") || (isBenchmarkEnabled ? get(portfolioBenchmark, "name") : null)
  const isCompositeBase = isCompositeBaseHelper(selectedBase)
  const compositeTooltip = selectedBase ? getCompositeSecurityTooltip(selectedBase, { modelSupportsComposites }) : ""

  const isETF = get(selectedBase, "type") === "ETF"
  const isETFWithMissingConstituents = isETF && (!modelSupportsComposites || !selectedBase?.hasComposition)
  const analyzeCompositeAvailable = isCompositeBase && !isETFWithMissingConstituents

  const openCompositeIcon = <Tooltip title={compositeTooltip}>
    <div className={classNames("composite-icon-wrapper", { "drop-down-visible": baseDropdownVisible, "alternate-layout": alternateLayout })}>
      <IconButton
        onClick={() => openCompositeSecurity(selectedBase)}
        disabled={!analyzeCompositeAvailable}
        size="large"
      >
        <OpenInNew fontSize="small" />
      </IconButton>
    </div>
  </Tooltip>

  const placeholderText = selectedBaseName ? `${alternateLayout ? "Active to " : ""}${selectedBaseName}` : SELECT_BENCHMARK_PLACEHOLDER

  const handleBaseDropdown = useCallback(() => {
    if (baseDropdownVisible) {
      baseInputBlur()
    } else {
      baseInputFocus()
    }
  }, [baseDropdownVisible, baseInputBlur, baseInputFocus])

  return (
    <div className={classNames("components_base-selector_base-search", { "alternate-layout": alternateLayout, "portfolio-layout": !useDefaultActiveBenchmarkSelector })}>
      <BenchmarkSelector
        isPortfolio={!useDefaultActiveBenchmarkSelector}
        placeholderText={placeholderText}
        handleBaseDropdown={handleBaseDropdown}
        alternateLayout={alternateLayout}
        toggleActive={toggleActive}
        isCompositeBase={isCompositeBase}
        baseDropdownVisible={baseDropdownVisible}
        openCompositeIcon={openCompositeIcon}
        selectedBase={selectedBase}
      />
      <Results {...{ modelId, searchResults, baseDropdownVisible, searchValue, baseHistory, setSelectedBase, selectedBase, portfolioBenchmark, alternateLayout, baseSearch }} />
    </div>
  )
}

BaseSearch.propTypes = {
  baseSearch: PropTypes.func.isRequired,
  toggleActive: PropTypes.func.isRequired,
  searchResults: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  setSelectedBase: PropTypes.func.isRequired,
  portfolioBenchmark: PropTypes.object.isRequired,
  baseHistory: PropTypes.array.isRequired,
  selectedBase: PropTypes.object,
  baseInputFocus: PropTypes.func.isRequired,
  baseInputBlur: PropTypes.func.isRequired,
  baseDropdownVisible: PropTypes.bool.isRequired,
  alternateLayout: PropTypes.bool,
  openCompositeSecurity: PropTypes.func.isRequired,
  modelSupportsComposites: PropTypes.bool.isRequired,
  useDefaultActiveBenchmarkSelector: PropTypes.bool,
  modelId: PropTypes.string.isRequired,
}

export default BaseSearch
